import React from 'react';

import { useSelector } from 'react-redux';
import {
  Trans,
  useBi,
  useTranslation,
  useEnvironment,
} from '@wix/yoshi-flow-editor';
import { groupCreatePostClick } from '@wix/bi-logger-groups/v2';
import { useCurrentStateAndParams, useRouter } from '@wix/tpa-router/react';

import { GroupAppKey } from 'store/groups/types';
import {
  selectGroupIdBySlug,
  selectIsAppAvailable,
  selectMediaItems,
} from 'store/selectors';

import { useDidUpdate } from 'common/hooks';
import { useController } from 'common/context/controller';

import { EmptyState } from 'wui/EmptyState';
import { ErrorState } from 'wui/ErrorState';
import { Spinner } from 'wui/Spinner';
import { Button } from 'wui/Button';

import { MediaGallery } from './Gallery';
import { WIDGET_MEDIA_EMPTY_STATE } from './dataHooks';
import { RestrictedState } from '../RestrictedState';

export function MediaPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const { isMobile } = useEnvironment();
  const { params } = useCurrentStateAndParams();
  const bi = useBi();

  const { group$, media$ } = useController();
  const { items, loading, error } = useSelector(selectMediaItems);
  const groupId = useSelector(selectGroupIdBySlug(params.slug));

  const isAvailable = useSelector(
    selectIsAppAvailable({
      groupId,
      application: GroupAppKey.GALLERY_APP,
    }),
  );

  useDidUpdate(() => {
    if (isAvailable) {
      media$.fetch(groupId, isMobile);
    }
  }, [isAvailable]);

  if (loading) {
    return <EmptyState title={<Spinner />} />;
  }

  if (!isAvailable) {
    return <RestrictedState application={GroupAppKey.GALLERY_APP} />;
  }

  if (error) {
    return (
      <ErrorState
        wired
        title={t('groups-web.discussion.error-state.title')}
        subtitle={
          <Trans
            i18nKey={t('groups-web.discussion.error-state.owner.message')}
            components={[<span key={0} />, <br key={1} />, <span key={2} />]}
          />
        }
        actionLabel={t('groups-web.discussion.error-state.cta')}
        onRetry={handleRetry}
      />
    );
  }

  if (!items.length) {
    return (
      <EmptyState
        wired
        data-hook={WIDGET_MEDIA_EMPTY_STATE}
        title={t('groups-web.media.empty.title')}
        subtitle={t('groups-web.media.empty.description')}
        action={
          <Button onClick={onCreatePost}>
            {t('groups-web.discussion.first.button')}
          </Button>
        }
      />
    );
  }

  return <MediaGallery items={items} />;

  function handleRetry() {
    media$.fetch(groupId, isMobile);
  }

  function onCreatePost() {
    bi.report(
      groupCreatePostClick({
        groupId,
        origin: 'media_tab_btn',
      }),
    );

    router.go('group.discussion.feed');
    group$.openCreatePostDialog(groupId);
  }
}

MediaPage.displayName = 'MediaPage';
